import {ComponentType, LazyExoticComponent} from 'react';
import {TFunction} from 'i18next';
import {RouteType} from '@/types';
import {i18nKey} from '@/locales/i18n';
import {ELEMENT_KEY_COMPONENT, PageRoute} from '@/constants';

const APP_NAME = process.env.APP_NAME ?? 'BnK';
export const titlePage = (title: string) => `${APP_NAME} - ${title}`;

export const createPublicRoutes = (
  t: TFunction<'translation', undefined>,
  lazyLoadRoute: (title: string) => LazyExoticComponent<ComponentType<unknown>>,
): RouteType[] => [
  {
    path: PageRoute.LOGIN,
    title: titlePage(t(i18nKey.metaTitle.login)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.base),
    children: [
      {
        index: true,
        title: titlePage(t(i18nKey.metaTitle.login)),
        element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.authLogin),
      },
    ],
  },
  {
    path: PageRoute.FORGOT_PASSWORD,
    title: titlePage(t(i18nKey.metaTitle.forgotPassword)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.base),
    children: [
      {
        index: true,
        title: titlePage(t(i18nKey.metaTitle.forgotPassword)),
        element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.authForgotPass),
      },
    ],
  },
  {
    path: PageRoute.RESET_PASSWORD,
    title: titlePage(t(i18nKey.metaTitle.resetPassword)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.base),
    children: [
      {
        index: true,
        title: titlePage(t(i18nKey.metaTitle.resetPassword)),
        element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.authResetPass),
      },
    ],
  },
  {
    path: PageRoute.LOGIN_SSO_CALLBACK,
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.authSSOCallback),
    title: titlePage(t(i18nKey.metaTitle.loginSSO)),
    children: [
      {
        index: true,
        title: titlePage(t(i18nKey.metaTitle.loginSSO)),
        element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.authSSOCallback),
      },
    ],
  },
  {
    path: PageRoute.ACTIVATE,
    title: titlePage(t(i18nKey.metaTitle.verification)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.base),
    children: [
      {
        index: true,
        title: titlePage(t(i18nKey.metaTitle.verification)),
        element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.activate),
      },
    ],
  },
];

export const createPrivateRoutes = (
  t: TFunction<'translation', undefined>,
  lazyLoadRoute: (title: string) => LazyExoticComponent<ComponentType<unknown>>,
): RouteType[] => [
  {
    path: '/metadata',
    title: titlePage(t(i18nKey.metaTitle.metadata)),
    element: lazyLoadRoute('Metadata'),
  },
  {
    path: PageRoute.HOME,
    title: titlePage(t(i18nKey.metaTitle.home)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.home),
  },
  {
    path: PageRoute.ACCOUNT,
    title: titlePage(t(i18nKey.metaTitle.account)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.account),
  },
  {
    path: PageRoute.DEPARTMENTS,
    title: titlePage(t(i18nKey.metaTitle.departments)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.departments),
  },
  {
    path: `${PageRoute.DEPARTMENTS}/usage-detail`,
    title: titlePage(t(i18nKey.metaTitle.departmentUsageDetail)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.departmentUsageDetail),
  },
  {
    path: PageRoute.DEPARTMENTS_ADD,
    title: titlePage(t(i18nKey.metaTitle.departmentAdd)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.departmentAddOrEdit),
  },
  {
    path: `${PageRoute.DEPARTMENTS_EDIT}/:id`,
    title: titlePage(t(i18nKey.metaTitle.departmentEdit)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.departmentAddOrEdit),
  },
  {
    path: `${PageRoute.DEPARTMENTS_DETAIL}/:id`,
    title: titlePage(t(i18nKey.metaTitle.departmentDetail)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.departmentDetail),
  },
  {
    path: PageRoute.USERS,
    title: titlePage(t(i18nKey.metaTitle.users)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.users),
  },
  {
    path: PageRoute.USER_ADD,
    title: titlePage(t(i18nKey.metaTitle.userAdd)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.userAddOrEdit),
  },
  {
    path: `${PageRoute.USER_EDIT}/:id`,
    title: titlePage(t(i18nKey.metaTitle.userEdit)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.userAddOrEdit),
  },
  {
    path: `${PageRoute.USER_DETAIL}/:id`,
    title: titlePage(t(i18nKey.metaTitle.userDetail)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.userDetail),
  },
  {
    path: PageRoute.CONFIG_BILLING,
    title: titlePage('Billing Settings'),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.billingSetting),
  },
  {
    path: PageRoute.REQUEST,
    title: titlePage(t(i18nKey.metaTitle.processingRequest)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.request),
  },
  {
    path: PageRoute.PROCESSING_LIST,
    title: titlePage(t(i18nKey.metaTitle.processingList)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.processingList),
  },
  {
    path: `${PageRoute.PROCESSING_REVIEW}/:id`,
    title: titlePage(t(i18nKey.metaTitle.processingReview)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.processingReview),
  },
  {
    path: PageRoute.USAGE,
    title: titlePage(t(i18nKey.metaTitle.usage)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.usage),
  },
  {
    path: `${PageRoute.USAGE_DETAIl}`,
    title: titlePage(t(i18nKey.metaTitle.departmentUsageDetail)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.usageDetail),
  },
  {
    path: PageRoute.INVOICE,
    title: titlePage(t(i18nKey.metaTitle.invoice)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.invoice),
  },
  {
    path: `${PageRoute.INVOICE_DETAIL}/:id`,
    title: titlePage(t(i18nKey.metaTitle.invoiceDetail)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.invoiceDetail),
  },
  {
    path: `${PageRoute.DOCUMENT_TEMPLATE}`,
    title: titlePage(t(i18nKey.metaTitle.documentTemplate)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.documentTemplate),
  },
  {
    path: PageRoute.DOCUMENT_TEMPLATE_ADD,
    title: titlePage(t(i18nKey.metaTitle.documentTemplateAdd)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.documentTemplateAddOrEdit),
  },
  {
    path: `${PageRoute.DOCUMENT_TEMPLATE_EDIT}/:id`,
    title: titlePage(t(i18nKey.metaTitle.documentTemplateEdit)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.documentTemplateAddOrEdit),
  },
  {
    path: `${PageRoute.DOCUMENT_TEMPLATE_DETAIL}/:id`,
    title: titlePage(t(i18nKey.metaTitle.documentTemplateDetail)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.documentTemplateDetail),
  },
  {
    path: `${PageRoute.SERVICE_HISTORY}`,
    title: titlePage(t(i18nKey.metaTitle.serviceHistory)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.serviceHistory),
  },
  {
    path: `${PageRoute.SUPPORT}`,
    title: titlePage(t(i18nKey.metaTitle.support)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.support),
  },
  {
    path: `${PageRoute.REQUEST_WORKFLOW}`,
    title: titlePage(t(i18nKey.metaTitle.requestWorkFlow)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.requestWorkflow),
  },
  {
    path: `${PageRoute.WORKFLOW}`,
    title: titlePage(t(i18nKey.metaTitle.workFlow)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.workFlows),
  },
  {
    path: `${PageRoute.PREVIEW_WORKFLOW}/:id`,
    title: titlePage(t(i18nKey.metaTitle.workFlow)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.previewWorkflow),
  },
  {
    path: `${PageRoute.WORK_FLOWS_DETAIL}/:id`,
    title: titlePage(t(i18nKey.metaTitle.workFlow)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.workFlowDetail),
  },
  {
    path: `${PageRoute.METADATA_DETAIL}/:id`,
    title: titlePage(t(i18nKey.metaTitle.metadata)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.metadataDetail),
  },
  {
    path: PageRoute.LOGGING,
    title: titlePage(t(i18nKey.metaTitle.logging)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.logging),
  },
  {
    path: PageRoute.MBAL_DOCSET_MAP,
    title: titlePage(t(i18nKey.metaTitle.mbalDocsetMap)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.docsetMap),
  },
  {
    path: PageRoute.MBAL_TEMPLATE_MAP,
    title: titlePage(t(i18nKey.metaTitle.mbalTemplateMap)),
    element: lazyLoadRoute(ELEMENT_KEY_COMPONENT.templateMap),
  },
];
